import { namePattern, requiredAddress, requiredValidation } from './validations'
import { TFunction } from 'next-i18next'
import { AddressFieldSchema, FieldType, InputFieldSchema, PhoneFieldSchema, Schema, CheckboxFieldSchema, DropdownFieldSchema } from '@afterpaytouch/form'
import { Country, ErrorCode } from '@afterpaytouch/portal-api/types'
import { AllCountryCode, CONFIG, InputAddressData } from '@afterpaytouch/core'
import { app } from '../../env'
import { ShippingAddress, ShippingAddressFormData } from '../../model'
import { getAddressOptionalFields, isValidAddress } from '../../utils/profile-validation'
import { isUndefinedOrEmpty } from '@afterpay/utils/string'
import { ShippingCountryOptions } from '../../model/checkout'
import { filterHighRiskCountries } from '../../utils/high-risk-country'

export const ALLOWED_ADDRESS_TYPES = ['street_address', 'subpremise', 'premise']

interface GetSchemaAndFormDataResult {
  schema: Schema<ShippingAddressFormSchemaData>
  initialFormData: ShippingAddressFormSchemaData
}

export interface ShippingAddressFormSchemaData {
  fullContactName: string
  mobile: string
  address: InputAddressData
  isPreferredAddress: boolean
  footer?: boolean
  shippingCountry?: string
}

interface ShippingAddressComponents {
  footer: () => JSX.Element
  intlShipping?: ShippingCountryOptions
  preferredLocale: string
  handleManualModeChange: (isManualMode: boolean) => void
  defaultShippingValue: string
}

const getInputFieldSchema = ({
  value,
  label,
  error = false,
  otherProps = {},
  options = {},
  inline = false,
  disabled = false,
  show = true,
}): InputFieldSchema => ({
  type: FieldType.Input,
  show,
  props: {
    label,
    value,
    error,
    disabled,
    ...otherProps,
  },
  options,
  inline,
})

const getPhoneFieldSchema = ({
  value,
  error = false,
  otherProps = {},
  options = {},
  otherValidations = {},
  validateOnLoad = false,
  disabled,
  show = true,
}): PhoneFieldSchema => ({
  type: FieldType.Phone,
  show,
  validateOnLoad,
  props: {
    value,
    disabled,
    ...otherProps,
  },
  options,
  ...otherValidations,
})

const getAddressFieldSchema = ({ value, error = false, otherProps = {}, options = {}, validateOnLoad = false, disabled, show = true }): AddressFieldSchema => ({
  type: FieldType.Address,
  apiKey: app.GOOGLEMAPS_API_KEY,
  show,
  validateOnLoad,
  props: {
    initialAddress: value,
    error,
    disabled,
    ...otherProps,
  },
  options,
})

const getCheckboxFieldSchema = ({ value, error = false, otherProps = {}, options = {}, show = true }): CheckboxFieldSchema => ({
  type: FieldType.Checkbox,
  show,
  props: {
    defaultChecked: value,
    ...otherProps,
  },
  options,
})

export const getDropdownFieldSchema = ({
  label,
  value = '',
  resetOnSelect = false,
  items,
  options = {},
  error = false,
  otherProps = {},
  show = false,
}): DropdownFieldSchema => ({
  type: FieldType.Dropdown,
  show,
  props: {
    label,
    defaultValue: value,
    error,
    ...otherProps,
  },
  items,
  options,
})

const getFormData = (initialValues: ShippingAddressFormData): ShippingAddressFormSchemaData => {
  const { fullContactName, address1, address2, area2, suburb, postcode, state, countryCode, mobile, isPreferredAddress } = initialValues

  const address = { address1, address2, area2, suburb, postcode, state, countryCode }

  return {
    fullContactName: fullContactName ?? '',
    mobile: mobile ?? '',
    address: (address as InputAddressData) ?? { address1: '', address2: '', area2: '', suburb: '', state: '', postcode: '', countryCode: '' },
    isPreferredAddress: isPreferredAddress ?? false,
  }
}

export const getSchemaAndFormData = (
  components: ShippingAddressComponents,
  t: TFunction,
  country: AllCountryCode,
  consumerCountry: Country,
  initialFormValues: ShippingAddressFormData,
  editMode: boolean,
  addressToEdit: ShippingAddress | undefined,
  phoneNumber: string | undefined,
  error: ErrorCode | undefined,
  isInternationalShippingEnabled: boolean,
  isCashTheme: boolean
): GetSchemaAndFormDataResult => {
  const initialFormData = getFormData(initialFormValues)
  const { fullContactName, address, isPreferredAddress } = initialFormData
  const locale = isUndefinedOrEmpty(components?.preferredLocale) ? [] : [components?.preferredLocale]
  const countryNameList = new Intl.DisplayNames(locale, { type: 'region' })

  const showForInternationalShipping = !editMode && isInternationalShippingEnabled && isUndefinedOrEmpty(country)

  const shippingCountry = editMode ? address.countryCode : country

  const allowedCountry = {
    countryCode: shippingCountry,
    name: isUndefinedOrEmpty(shippingCountry) ? shippingCountry : countryNameList.of(shippingCountry),
  }

  const isCountryMappedForAutocomplete = shippingCountry in CONFIG

  const required = requiredValidation(t)
  const namePatterns = namePattern(t)
  const highRiskCountryFilter = filterHighRiskCountries(consumerCountry)

  const transformCountries = (intlCountryList): string[] => {
    const list = intlCountryList.map((shippingCountryCode) => {
      return {
        key: shippingCountryCode,
        label: countryNameList.of(shippingCountryCode),
      }
    })

    return list
  }

  const populateDropdown = (intlShippingOption): string[] | undefined => {
    if (!isInternationalShippingEnabled) {
      return undefined
    }

    const supportedShippingCountries = intlShippingOption?.supportedShippingCountries ?? []

    const filteredSupportedShippingCountries = highRiskCountryFilter(supportedShippingCountries)

    if (editMode && isInternationalShippingEnabled) {
      const currentAddressCountry = address.countryCode as AllCountryCode
      const intlCountryList = filteredSupportedShippingCountries.includes(currentAddressCountry)
        ? filteredSupportedShippingCountries
        : filteredSupportedShippingCountries.concat(currentAddressCountry)
      return transformCountries(intlCountryList)
    }
    return transformCountries(filteredSupportedShippingCountries)
  }

  const schema: Schema<ShippingAddressFormSchemaData> = {
    shippingCountry: getDropdownFieldSchema({
      label: t('addNewAddress:countryLabel'),
      value: editMode ? address.countryCode : components?.defaultShippingValue,
      items: populateDropdown(components?.intlShipping),
      show: isInternationalShippingEnabled,
      otherProps: {
        disabled: editMode,
        'data-dd-action-name': 'Shipping Address Shipping Country Input',
      },
    }),
    fullContactName: getInputFieldSchema({
      value: fullContactName,
      show: !showForInternationalShipping,
      label: t('addNewAddress:fullContactNameInputLabel'),
      otherProps: {
        capitalize: true,
        autoComplete: 'name',
        disabled: editMode,
        'data-dd-action-name': 'Shipping Address Full Contact Name Input',
      },
      options: {
        ...required('fullContactName'),
        ...namePatterns('fullContactName'),
      },
    }),
    mobile: getPhoneFieldSchema({
      value: phoneNumber,
      disabled: editMode && !isUndefinedOrEmpty(addressToEdit?.phoneNumber),
      show: !showForInternationalShipping,
      validateOnLoad: editMode && isUndefinedOrEmpty(addressToEdit?.phoneNumber),
      otherProps: {
        ...(!editMode && { key: country }),
        label: t('addNewAddress:mobileNumberInputLabel'),
        allowedCountries: [allowedCountry],
        selectedCountry: country,
        error: error === ErrorCode.InvalidPhoneNumber,
        singleCountryShowEmoji: isInternationalShippingEnabled,
        'data-dd-action-name': 'Shipping Address Mobile Input',
      },
      options: {
        ...required('mobileNumber'),
      },
      otherValidations: {
        invalid: t('addNewAddress:error:invalid', { context: 'mobileNumber' }),
      },
    }),
    address: getAddressFieldSchema({
      value: address,
      disabled: editMode && isValidAddress(addressToEdit),
      show: !showForInternationalShipping,
      validateOnLoad: editMode && !isValidAddress(addressToEdit),
      otherProps: {
        key: shippingCountry,
        label: t('addNewAddress:addressInputLabel'),
        manualAddressLabel: t('addNewAddress:findAddressLabel'),
        autoFocus: !editMode && !isInternationalShippingEnabled,
        address1Label: t('addNewAddress:address1Label'),
        address2Label: t('addNewAddress:address2Label'),
        postcodeLabel: t('addNewAddress:postcodeLabel'),
        componentRestrictions: { country: shippingCountry },
        suburbLabel: isCountryMappedForAutocomplete ? t('addNewAddress:suburbLabel') : t('addNewAddress:genericSuburbLabel'),
        stateLabel: isCountryMappedForAutocomplete ? t('addNewAddress:stateLabel') : t('addNewAddress:genericStateLabel'),
        optionalManualAddressFields: getAddressOptionalFields(shippingCountry),
        manualAddressLabelCaption: t('addNewAddress:findAddressLabelCaption'),
        onManualModeChange: (isManualMode) => components.handleManualModeChange(isManualMode),
        isFullPageSuggestionsEnabled: true,
        icon: 'Search',
        iconPosition: 'Left',
        showManualAddressLabelInMenu: true,
        showManualAddressFieldsOnSelect: true,
        allowedAddressType: ALLOWED_ADDRESS_TYPES,
        ...(!editMode &&
          isCountryMappedForAutocomplete && {
            searchAddressLabel: t('addNewAddress:searchAddressLabel'),
          }),
        ...(!editMode && { address2InfoText: t('addNewAddress:address2InfoText') }),
        defaultExpanded: editMode || !isCountryMappedForAutocomplete,
      },
      options: {
        validate: {
          ...requiredAddress({ name: 'address', country: shippingCountry, t }),
        },
      },
    }),
    isPreferredAddress: getCheckboxFieldSchema({
      value: isPreferredAddress,
      show: !(editMode || showForInternationalShipping),
      otherProps: {
        label: t('addNewAddress:preferredAddressInputLabel'),
        size: isCashTheme ? 'Medium' : 'Small',
        bold: true,
        'data-dd-action-name': 'Shipping Address Is Preffered Address Checkbox',
      },
    }),
    footer: {
      type: FieldType.Component,
      show: !showForInternationalShipping,
      Component: components.footer,
    },
  }

  return { schema, initialFormData }
}
